import React, { useState, useEffect } from 'react';
import { Property, Unit, Application, Prospect } from '../../types';
import { PropertySelect } from '../ui/PropertySelect';
import { ProspectSelect } from '../ui/ProspectSelect';
import { capitalizeWords, isValidEmail, formatCurrencyInput, parseCurrencyToNumber } from '../../utils/validation';
import { formatDate, todayLocalDateString } from '../../utils/date';

const COUNTRY_PHONE_CODES: { code: string; flag: string; label: string }[] = [
  { code: '+52', flag: '🇲🇽', label: 'México' },
  { code: '+1', flag: '🇺🇸', label: 'Estados Unidos' },
  { code: '+34', flag: '🇪🇸', label: 'España' },
  { code: '+57', flag: '🇨🇴', label: 'Colombia' },
  { code: '+54', flag: '🇦🇷', label: 'Argentina' },
  { code: '+51', flag: '🇵🇪', label: 'Perú' },
  { code: '+56', flag: '🇨🇱', label: 'Chile' },
  { code: '+58', flag: '🇻🇪', label: 'Venezuela' },
  { code: '+593', flag: '🇪🇨', label: 'Ecuador' },
  { code: '+595', flag: '🇵🇾', label: 'Paraguay' },
  { code: '+598', flag: '🇺🇾', label: 'Uruguay' },
  { code: '+502', flag: '🇬🇹', label: 'Guatemala' },
  { code: '+503', flag: '🇸🇻', label: 'El Salvador' },
  { code: '+504', flag: '🇭🇳', label: 'Honduras' },
  { code: '+505', flag: '🇳🇮', label: 'Nicaragua' },
  { code: '+506', flag: '🇨🇷', label: 'Costa Rica' },
  { code: '+507', flag: '🇵🇦', label: 'Panamá' },
  { code: '+55', flag: '🇧🇷', label: 'Brasil' },
];

function parsePhoneToCountryAndNumber(phone: string): { countryCode: string; number: string } {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return { countryCode: '+52', number: '' };
  const sortedCodes = [...COUNTRY_PHONE_CODES].sort((a, b) => b.code.replace(/\D/g, '').length - a.code.replace(/\D/g, '').length);
  for (const { code } of sortedCodes) {
    const codeDigits = code.replace(/\D/g, '');
    if (digits.startsWith(codeDigits)) {
      const number = digits.slice(codeDigits.length).slice(0, 10);
      return { countryCode: code, number };
    }
  }
  return { countryCode: '+52', number: digits.slice(-10) };
}

interface ApplicationFormProps {
  application?: Application;
  properties: Property[];
  units: Unit[];
  prospects?: Prospect[];
  onSubmit: (
    application: Omit<Application, 'id' | 'prospectId' | 'prospectName' | 'propertyName'> & {
      firstName: string;
      lastName: string;
      monthlyRent: number;
      tipoUso: 'Habitacional' | 'Comercial';
      selectedProspectId?: string;
      email?: string;
      phone?: string;
    },
  ) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const ApplicationForm: React.FC<ApplicationFormProps> = ({
  application,
  properties,
  units,
  prospects = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  // Extraer nombre y apellido del prospectName si existe
  const getNamesFromProspect = (prospectName?: string) => {
    if (!prospectName) return { firstName: '', lastName: '' };
    const parts = prospectName.split(' ');
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
    };
  };

  // Extraer monto de las notas si existe (soporta decimales ej. $15,000.50)
  const getMonthlyRentFromNotes = (notes?: string) => {
    if (!notes) return 0;
    const match = notes.match(/\$([\d,]+(?:\.\d{1,2})?)/);
    if (match) {
      return parseFloat(match[1].replace(/,/g, '')) || 0;
    }
    return 0;
  };

  const { firstName: initialFirstName, lastName: initialLastName } = getNamesFromProspect(application?.prospectName);
  const initialMonthlyRent = application ? getMonthlyRentFromNotes(application.notes) : 0;

  const [formData, setFormData] = useState({
    selectedProspectId: application?.prospectId || '',
    propertyId: application?.propertyId || '',
    unitNumber: application?.unitNumber || '',
    firstName: initialFirstName,
    lastName: initialLastName,
    email: '',
    whatsappCountryCode: '+52',
    whatsappNumber: '',
    monthlyRent: initialMonthlyRent,
    contractMonths: application?.contractMonths ?? 13,
    tipoUso: 'Habitacional' as 'Habitacional' | 'Comercial',
    submittedDate: application?.submittedDate || todayLocalDateString(),
    status: application?.status || 'pending' as 'pending' | 'approved' | 'rejected',
  });

  const [amountDisplay, setAmountDisplay] = useState(initialMonthlyRent > 0 ? String(initialMonthlyRent) : '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [useExistingProspect, setUseExistingProspect] = useState(!!application?.prospectId);

  const selectedProperty = properties.find(p => p.id === formData.propertyId);
  const selectedProspect = prospects.find(p => p.id === formData.selectedProspectId);
  const availableUnits = units.filter(
    u => u.propertyId === formData.propertyId && (u.status === 'available' || u.status === 'reserved')
  );

  const inferTipoUsoFromNotes = (notes?: string): 'Habitacional' | 'Comercial' | '' => {
    if (!notes) return '';
    const lower = notes.toLowerCase();
    if (lower.includes('habitacional')) return 'Habitacional';
    if (lower.includes('comercial')) return 'Comercial';

    try {
      const parsed = JSON.parse(notes);
      if (parsed?.tipoUso === 'Habitacional' || parsed?.tipoUso === 'Comercial') return parsed.tipoUso;
      if (parsed?.propertyType === 'habitacional') return 'Habitacional';
      if (parsed?.propertyType === 'comercial') return 'Comercial';
    } catch (_) {
      // no-op
    }
    return '';
  };

  // Cuando cambie la unidad, prellenar tipo de uso desde la unidad (si existe).
  useEffect(() => {
    if (!formData.propertyId || !formData.unitNumber) return;
    const selectedUnit = units.find(u => u.propertyId === formData.propertyId && u.unitNumber === formData.unitNumber);
    if (!selectedUnit) return;

    setFormData(prev => ({
      ...prev,
      tipoUso: inferTipoUsoFromNotes(selectedUnit.notes) || prev.tipoUso,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.propertyId, formData.unitNumber]);

  // Cuando se selecciona un prospecto, prellenar datos
  useEffect(() => {
    if (selectedProspect && useExistingProspect) {
      const names = selectedProspect.name.split(' ');
      const { countryCode, number } = parsePhoneToCountryAndNumber(selectedProspect.phone || '');
      setFormData(prev => ({
        ...prev,
        firstName: names[0] || '',
        lastName: names.slice(1).join(' ') || '',
        email: selectedProspect.email || '',
        whatsappCountryCode: countryCode,
        whatsappNumber: number,
        propertyId: selectedProspect.propertyId || prev.propertyId,
      }));
    } else if (!useExistingProspect) {
      setFormData(prev => ({
        ...prev,
        email: '',
        whatsappCountryCode: '+52',
        whatsappNumber: '',
      }));
    }
  }, [selectedProspect, useExistingProspect]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (useExistingProspect && !formData.selectedProspectId) {
      newErrors.selectedProspectId = 'Debe seleccionar un prospecto';
    }
    if (!formData.propertyId) {
      newErrors.propertyId = 'Debe seleccionar una propiedad';
    }
    if (!formData.unitNumber) {
      newErrors.unitNumber = 'Debe seleccionar una unidad';
    }
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'El correo electrónico no es válido';
    }
    if (!formData.whatsappNumber.trim()) {
      newErrors.whatsappNumber = 'El WhatsApp es requerido';
    } else if (formData.whatsappNumber.replace(/\D/g, '').length !== 10) {
      newErrors.whatsappNumber = 'El número debe tener 10 dígitos';
    }
    if (formData.monthlyRent <= 0) {
      newErrors.monthlyRent = 'El monto debe ser mayor a 0';
    }
    if (!formData.contractMonths || formData.contractMonths < 1 || !Number.isInteger(formData.contractMonths)) {
      newErrors.contractMonths = 'La duración debe ser un entero positivo';
    }
    if (!formData.submittedDate) {
      newErrors.submittedDate = 'La fecha es requerida';
    } else if (!application && formData.submittedDate < todayLocalDateString()) {
      newErrors.submittedDate = 'La fecha no puede ser anterior a hoy';
    }
    if (!formData.tipoUso) {
      newErrors.tipoUso = 'El tipo de uso es requerido';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const phone = formData.whatsappCountryCode.replace(/\D/g, '') + formData.whatsappNumber.replace(/\D/g, '');
    onSubmit({
      propertyId: formData.propertyId,
      unitNumber: formData.unitNumber,
      submittedDate: formData.submittedDate,
      status: formData.status,
      monthlyRent: formData.monthlyRent,
      contractMonths: formData.contractMonths,
      tipoUso: formData.tipoUso,
      notes: `Solicitud de ${formData.firstName} ${formData.lastName} para unidad ${formData.unitNumber}. Monto: $${formData.monthlyRent.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
      firstName: formData.firstName,
      lastName: formData.lastName,
      selectedProspectId: formData.selectedProspectId,
      email: formData.email,
      phone,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Selector de Prospecto Existente o Nuevo */}
      {!application && prospects.length > 0 && (
        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <label className="flex items-center space-x-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={useExistingProspect}
                onChange={(e) => {
                  setUseExistingProspect(e.target.checked);
                  if (!e.target.checked) {
                    setFormData(prev => ({ ...prev, selectedProspectId: '', firstName: '', lastName: '', email: '', whatsappCountryCode: '+52', whatsappNumber: '', propertyId: '' }));
                  }
                }}
                className="w-4 h-4 bg-white border-2 border-gray-500 rounded text-primary appearance-none checked:bg-primary checked:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                style={{ colorScheme: 'light' }}
              />
              {useExistingProspect && (
                <svg
                  className="absolute top-0 left-0 w-4 h-4 pointer-events-none"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="3"
                  style={{ color: '#FA4999' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
            <span className="text-sm font-medium text-gray-700">
              Usar prospecto existente de la base de datos
            </span>
          </label>
        </div>
      )}

      {useExistingProspect && prospects.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prospecto <span className="text-red-500">*</span>
          </label>
          <ProspectSelect
            prospects={prospects}
            value={formData.selectedProspectId}
            onChange={(prospectId) => {
              const prospect = prospects.find(p => p.id === prospectId);
              if (prospect) {
                const names = prospect.name.split(' ');
                const { countryCode, number } = parsePhoneToCountryAndNumber(prospect.phone || '');
                setFormData(prev => ({
                  ...prev,
                  selectedProspectId: prospectId,
                  firstName: names[0] || '',
                  lastName: names.slice(1).join(' ') || '',
                  email: prospect.email || '',
                  whatsappCountryCode: countryCode,
                  whatsappNumber: number,
                  propertyId: prospect.propertyId || prev.propertyId,
                }));
              } else {
                setFormData(prev => ({
                  ...prev,
                  selectedProspectId: prospectId,
                }));
              }
            }}
            error={errors.selectedProspectId}
            placeholder="Seleccione un prospecto"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Propiedad */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Propiedad <span className="text-red-500">*</span>
          </label>
          <PropertySelect
            properties={properties}
            value={formData.propertyId}
            onChange={(propertyId) => {
              handleChange('propertyId', propertyId);
              handleChange('unitNumber', ''); // Resetear unidad cuando cambia la propiedad
            }}
            error={errors.propertyId}
            placeholder="Seleccione una propiedad"
            showPropertyCost={false}
          />
        </div>

        {/* Unidad */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unidad <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.unitNumber}
            onChange={(e) => handleChange('unitNumber', e.target.value)}
            disabled={!formData.propertyId || availableUnits.length === 0}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.unitNumber ? 'border-red-500' : 'border-gray-300'
            } ${!formData.propertyId || availableUnits.length === 0 ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          >
            <option value="">
              {!formData.propertyId
                ? 'Primero seleccione una propiedad'
                : availableUnits.length === 0
                ? 'No hay unidades disponibles'
                : 'Seleccione una unidad'}
            </option>
            {availableUnits.map((unit) => (
              <option key={unit.id} value={unit.unitNumber}>
                Unidad {unit.unitNumber} - {unit.m2}m²
                {unit.bedrooms && unit.bathrooms
                  ? ` - ${unit.bedrooms} hab, ${unit.bathrooms} baños`
                  : ''}
                {unit.status === 'reserved' ? ' (Reservada)' : ''}
              </option>
            ))}
          </select>
          {errors.unitNumber && <p className="mt-1 text-sm text-red-500">{errors.unitNumber}</p>}
        </div>

        {/* Nombre - primera letra de cada palabra en mayúscula (al dar espacio se repite) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', capitalizeWords(e.target.value))}
            onBlur={(e) => e.target.value && handleChange('firstName', capitalizeWords(e.target.value))}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = (e.clipboardData.getData('text') || '').trim();
              if (pasted) handleChange('firstName', capitalizeWords(pasted));
            }}
            disabled={useExistingProspect}
            readOnly={useExistingProspect}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.firstName ? 'border-red-500' : 'border-gray-300'
            } ${useExistingProspect ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            placeholder="Ej: Juan Carlos"
          />
          {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
          {useExistingProspect && (
            <p className="mt-1 text-xs text-gray-500">
              Este campo se llena automáticamente al seleccionar un prospecto
            </p>
          )}
        </div>

        {/* Apellido - primera letra de cada palabra en mayúscula (al dar espacio se repite) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Apellido <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', capitalizeWords(e.target.value))}
            onBlur={(e) => e.target.value && handleChange('lastName', capitalizeWords(e.target.value))}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = (e.clipboardData.getData('text') || '').trim();
              if (pasted) handleChange('lastName', capitalizeWords(pasted));
            }}
            disabled={useExistingProspect}
            readOnly={useExistingProspect}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.lastName ? 'border-red-500' : 'border-gray-300'
            } ${useExistingProspect ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            placeholder="Ej: Pérez García"
          />
          {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
          {useExistingProspect && (
            <p className="mt-1 text-xs text-gray-500">
              Este campo se llena automáticamente al seleccionar un prospecto
            </p>
          )}
        </div>

        {/* Correo Electrónico */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Correo Electrónico <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value.trim())}
            onBlur={(e) => e.target.value && handleChange('email', e.target.value.trim())}
            disabled={useExistingProspect}
            readOnly={useExistingProspect}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            } ${useExistingProspect ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            placeholder="Ej: juan@ejemplo.com"
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          {useExistingProspect && (
            <p className="mt-1 text-xs text-gray-500">
              Este campo se llena automáticamente al seleccionar un prospecto
            </p>
          )}
        </div>

        {/* WhatsApp */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            WhatsApp <span className="text-red-500">*</span> <span className="text-gray-500 font-normal">(10 dígitos)</span>
          </label>
          <div className="flex gap-2">
            <select
              value={formData.whatsappCountryCode}
              onChange={(e) => handleChange('whatsappCountryCode', e.target.value)}
              disabled={useExistingProspect}
              className={`shrink-0 w-[120px] px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.whatsappNumber ? 'border-red-500' : 'border-gray-300'
              } ${useExistingProspect ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              title="Código de país"
            >
              {COUNTRY_PHONE_CODES.map(({ code, flag, label }) => (
                <option key={code} value={code}>
                  {flag} {code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              value={formData.whatsappNumber}
              onChange={(e) => {
                const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 10);
                handleChange('whatsappNumber', onlyDigits);
              }}
              disabled={useExistingProspect}
              readOnly={useExistingProspect}
              className={`flex-1 min-w-0 px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
                errors.whatsappNumber ? 'border-red-500' : 'border-gray-300'
              } ${useExistingProspect ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              placeholder="5551234567"
            />
          </div>
          {errors.whatsappNumber && <p className="mt-1 text-sm text-red-500">{errors.whatsappNumber}</p>}
          {useExistingProspect && (
            <p className="mt-1 text-xs text-gray-500">
              Este campo se llena automáticamente al seleccionar un prospecto
            </p>
          )}
        </div>

        {/* Monto del Contrato */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto del Contrato de Alquiler <span className="text-red-500">*</span> <span className="text-gray-500 font-normal">(solo números, un punto para decimales)</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amountDisplay}
              onChange={(e) => {
                const value = formatCurrencyInput(e.target.value);
                setAmountDisplay(value);
                handleChange('monthlyRent', parseCurrencyToNumber(value));
              }}
              onBlur={() => {
                const n = parseCurrencyToNumber(amountDisplay);
                setAmountDisplay(n > 0 ? n.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '');
              }}
              onFocus={() => {
                if (formData.monthlyRent > 0) {
                  setAmountDisplay(String(formData.monthlyRent));
                }
              }}
              className={`w-full pl-8 pr-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.monthlyRent ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ej: 15000 o 15000.50"
            />
          </div>
          {errors.monthlyRent && <p className="mt-1 text-sm text-red-500">{errors.monthlyRent}</p>}
        </div>

        {/* Fecha de Solicitud */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha de Solicitud <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            min={application ? undefined : todayLocalDateString()}
            value={formData.submittedDate}
            onChange={(e) => handleChange('submittedDate', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.submittedDate ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.submittedDate && <p className="mt-1 text-sm text-red-500">{errors.submittedDate}</p>}
        </div>

        {/* Contrato: Tipo de Uso y Duración */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Uso <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.tipoUso}
            onChange={(e) => handleChange('tipoUso', e.target.value)}
            className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="Habitacional">Habitacional</option>
            <option value="Comercial">Comercial</option>
          </select>
          {errors.tipoUso && <p className="mt-1 text-sm text-red-500">{errors.tipoUso}</p>}
        </div>

        {/* Duración del Contrato */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Duración del Contrato (meses) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={formData.contractMonths}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val >= 1) {
                handleChange('contractMonths', val);
              } else if (e.target.value === '') {
                handleChange('contractMonths', '');
              }
            }}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.contractMonths ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: 13"
          />
          {errors.contractMonths && <p className="mt-1 text-sm text-red-500">{errors.contractMonths}</p>}
        </div>

        {/* Estado (solo para edición) */}
        {application && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="pending">Pendiente</option>
              <option value="approved">Aprobada</option>
              <option value="rejected">Rechazada</option>
            </select>
          </div>
        )}
      </div>

      {/* Resumen */}
      {formData.propertyId && formData.unitNumber && formData.firstName && formData.lastName && (
        <div className="p-4 bg-primary/10 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Resumen de la Solicitud</h4>
          <div className="space-y-1 text-sm text-gray-700">
            <div className="flex justify-between">
              <span>Prospecto:</span>
              <span className="font-medium">{formData.firstName} {formData.lastName}</span>
            </div>
            <div className="flex justify-between">
              <span>Propiedad:</span>
              <span className="font-medium">{selectedProperty?.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Unidad:</span>
              <span className="font-medium">{formData.unitNumber}</span>
            </div>
            <div className="flex justify-between">
              <span>Monto mensual:</span>
              <span className="font-medium">${formData.monthlyRent.toLocaleString('es-MX')}</span>
            </div>
            <div className="flex justify-between">
              <span>Fecha:</span>
              <span className="font-medium">
                {formatDate(formData.submittedDate)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Guardando...' : application ? 'Guardar Cambios' : 'Crear Solicitud'}
        </button>
      </div>
    </form>
  );
};

